import React, {Suspense, useEffect, useMemo, useState, lazy, useCallback} from 'react';
import QuizScoreCard from '../../answer-sheet/components/QuizScoreCard';
import PendingValidationCard from '../../answer-sheet/components/PendingValidationCard';
import {
    getQuestionCategory, QUESTION_CATEGORY, QUESTION_TYPE
} from '../../shared/utils/constants';
import {getAnswerSheetDataFromQuizData} from '../../answer-sheet/utils/answer-sheet-data';
import {getDateWithTime} from '../../shared/utils/get-date-with-time';
import {decryptAnswerSheetPayload} from '../../shared/utils/answer-sheet-decrypt';
import ShareButton from './ShareButton';
import '../css/learn-island.css';
import Loading from "../../shared/components/Loading";
import PrintButton from "./PrintButton";

const loadAnswerSheet = () => import('../../answer-sheet/components/AnswerSheet');
const AnswerSheet = lazy(loadAnswerSheet);

const FETCH_STATE = {
    IDLE: 'idle',
    LOADING: 'loading',
    LOADED: 'loaded',
    FORBIDDEN: 'forbidden',
    ERROR: 'error'
};

function getQuizServerBase() {
    return (typeof process !== 'undefined' && process.env && process.env.ENV_QUIZ_SERVER_URL) || '';
}

function fetchEncryptedAnswerSheet(answerId) {
    const base = getQuizServerBase();
    return fetch(base + '/v2/answer-sheet/' + encodeURIComponent(answerId) + '/encrypted', {
        method: 'GET',
        credentials: 'omit'
    }).then(async r => {
        if (r.status === 200) {
            const body = await r.json();
            if (body.allowed && body.encrypted_data) {
                return {ok: true, encrypted: body.encrypted_data};
            }
            return {ok: false, forbidden: true};
        }
        if (r.status === 403 || r.status === 404) {
            return {ok: false, forbidden: true};
        }
        return {ok: false, forbidden: false};
    }).catch(() => ({ok: false, forbidden: false}));
}

function LearnAnswerSheetIsland({answerId, slug, onAnswerSheetLoaded, onAnswerSheetUnavailable}) {
    const [state, setState] = useState(FETCH_STATE.IDLE);
    const [payload, setPayload] = useState(null);
    const [expanded, setExpanded] = useState(false);

    const waitForElement = useCallback((selector) => {
        return new Promise((resolve) => {
            const element = document.querySelector(selector);

            if (element) {
                resolve(element);
                return;
            }

            const observer = new MutationObserver(() => {
                const element = document.querySelector(selector);

                if (element) {
                    observer.disconnect();
                    resolve(element);
                }
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true,
            });
        });
    }, []);

    const handlePrint = useCallback(async () => {
        setExpanded(true);

        await waitForElement("#question-title-container");

        // Wait for the next paint so the DOM is fully rendered
        await new Promise(requestAnimationFrame);

        window.print();
    }, [waitForElement]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
                e.preventDefault();
                handlePrint();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [handlePrint]);

    useEffect(() => {
        let cancelled = false;
        if (!answerId) return undefined;
        setState(FETCH_STATE.LOADING);
        (async () => {
            const res = await fetchEncryptedAnswerSheet(answerId);
            if (cancelled) return;
            if (!res.ok) {
                if (typeof onAnswerSheetUnavailable === 'function') {
                    onAnswerSheetUnavailable(answerId, res.forbidden);
                }
                setState(res.forbidden ? FETCH_STATE.FORBIDDEN : FETCH_STATE.ERROR);
                return;
            }
            try {
                const decrypted = decryptAnswerSheetPayload(res.encrypted, answerId);
                if (cancelled) return;
                setPayload(decrypted);
                setState(FETCH_STATE.LOADED);
            } catch (e) {
                if (cancelled) return;
                setState(FETCH_STATE.ERROR);
            }
        })();
        return () => { cancelled = true; };
    }, [answerId, onAnswerSheetUnavailable]);

    const derived = useMemo(() => {
        if (!payload || !payload.quizQuestionData || !payload.quizAnswerData) return null;
        const quiz = payload.quizQuestionData;
        const answer = payload.quizAnswerData;
        const questions = quiz.pages && quiz.pages[0] ? quiz.pages[0].questions : [];
        const inputData = getAnswerSheetDataFromQuizData(answer, questions);
        return {
            quiz,
            answer,
            inputData,
            quizTitle: quiz.welcome_screen ? quiz.welcome_screen.title : '',
            submitTime: answer.submit_time,
            userInfo1Hint: quiz.quiz_data && quiz.quiz_data.user_info ? quiz.quiz_data.user_info.user_info_1 : null,
            userInfo1Value: answer.quiz_data && answer.quiz_data.user_info ? answer.quiz_data.user_info.user_info_1 : null,
        };
    }, [payload]);

    useEffect(() => {
        if (state !== FETCH_STATE.LOADED || !derived) return;
        if (typeof onAnswerSheetLoaded === 'function') {
            onAnswerSheetLoaded({
                quizId: derived.quiz && derived.quiz._id,
                answerId: derived.answer && derived.answer._id,
                quizSetting: derived.quiz && derived.quiz.setting,
                confirmationMessage: derived.quiz && derived.quiz.setting && derived.quiz.setting.confirmation_message
            });
        }
    }, [state, derived, onAnswerSheetLoaded]);

    useEffect(() => {
        let savedTitle = null;
        const onBeforePrint = () => {
            if (!derived) return; // sheet not loaded yet, leave the page title alone
            savedTitle = document.title; // capture the live (article) title to restore later
            document.title = `${derived.quizTitle || 'Your Quiz'}`;
        };
        const onAfterPrint = () => {
            if (savedTitle !== null) {
                document.title = savedTitle;
                savedTitle = null;
            }
        };
        window.addEventListener("beforeprint", onBeforePrint);
        window.addEventListener("afterprint", onAfterPrint);
        return () => {
            window.removeEventListener("beforeprint", onBeforePrint);
            window.removeEventListener("afterprint", onAfterPrint);
        };
    }, [derived]);

    const questionsData = derived?.inputData?.individualResponderQuestionWiseData;
    const isQuizV2 = derived?.quiz?.is_quizzory_v2;
    const isEvaluating = derived?.answer?.is_evaluating;

    const {isChoiceTypeAvailable, isChoiceTypeOnlyAvailable} = useMemo(() => {
        let available = false;
        let onlyAvailable = true;

        if (questionsData) {
            questionsData.forEach(summaryData => {
                if (summaryData.type !== QUESTION_TYPE.SECTION.key) {
                    if (getQuestionCategory(summaryData.type) === QUESTION_CATEGORY.CHOICE_TYPE) {
                        available = true;
                    } else {
                        onlyAvailable = false;
                    }
                }
            });
        }

        return {
            isChoiceTypeAvailable: available,
            isChoiceTypeOnlyAvailable: onlyAvailable
        };
    }, [questionsData]);

    const isValidated = useMemo(() => {
        if (!isQuizV2) return true;
        if (isEvaluating) return false;
        return questionsData ? questionsData.every(question => question.type === QUESTION_TYPE.SECTION.key || question.evaluated_marks !== undefined) : true;
    }, [isQuizV2, isEvaluating, questionsData]);

    if (state !== FETCH_STATE.LOADED || !derived || !derived.inputData) {
        // While loading / forbidden / error: render nothing. Top-ad position swap will react to this.
        return null;
    }

    const {quiz, answer, inputData, quizTitle, submitTime, userInfo1Hint, userInfo1Value} = derived;

    const renderQuizHeader = () => {
        if (isQuizV2) {
            return isValidated ? (<QuizScoreCard
                cardStyle={{width: '100%'}}
                isPublish={quiz.is_publish}
                isQuizEdit={false}
                quizResults={inputData.quizResults}
                isChoiceTypeOnlyAvailable={isChoiceTypeOnlyAvailable}
            />) : (<PendingValidationCard isQuizEdit={false}/>);
        } else {
            return isChoiceTypeAvailable ? (<QuizScoreCard
                cardStyle={{width: '100%'}}
                isQuizV1={true}
                isPublish={quiz.is_publish}
                isQuizEdit={false}
                quizResults={inputData.quizResults}
            />) : null;
        }
    };

    const onToggle = () => setExpanded(v => !v);

    return (
        <section className="learn-island" aria-label="Quiz results">
            <div className="learn-island__card">
                <header className="learn-island__header">
                    <div className="learn-island__title-row">
                        <h2 className="learn-island__title">{quizTitle || 'Your Quiz'}</h2>
                    </div>
                    {userInfo1Hint ? (
                        <p className="learn-island__user-info">
                            {userInfo1Hint}: {userInfo1Value || '-'}
                        </p>
                    ) : null}
                    {submitTime ? (
                        <p className="learn-island__date">{getDateWithTime(submitTime)}</p>
                    ) : null}
                    <div className="learn-island__divider"/>
                    <p className="learn-island__score-label">
                        <span>Your Score</span>

                        <span style={{position: "absolute", right: 0, display: "flex", gap: "10px"}}>
                            <PrintButton handlePrint={handlePrint}/>
                            <ShareButton slug={slug} quizId={quiz._id} answerId={answerId}/>
                        </span>
                    </p>
                </header>

                <div className="learn-island__score">
                    {renderQuizHeader()}
                </div>

                <button type="button"
                        className="learn-island__toggle"
                        onClick={onToggle}
                        aria-expanded={expanded}>
                    <span>{expanded ? 'Hide Answer Sheet' : 'View Detailed Answer Sheet'}</span>
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <polyline points={expanded ? '18 15 12 9 6 15' : '6 9 12 15 18 9'}></polyline>
                    </svg>
                </button>

                {expanded && (
                    <div className="learn-island__detail">
                        <Suspense fallback={<Loading/>}>
                            <AnswerSheet
                                isQuizEdit={false}
                                isQuiz={true}
                                hideQuizHeader={true}
                                userId={quiz.user_id}
                                responseCount={1}
                                subscriptionData={quiz.subscription_data}
                                isQuizV2={quiz.is_quizzory_v2}
                                individualResponderQuestionWiseData={inputData.individualResponderQuestionWiseData}
                                quizResults={inputData.quizResults}
                                isPublish={quiz.is_publish}
                                isEvaluating={answer.is_evaluating}
                                showQuizCorrectAnswer={quiz.setting && quiz.setting.is_show_correct_answer}
                                answerData={answer}
                                quizId={quiz._id}
                            />
                        </Suspense>
                    </div>
                )}
            </div>
        </section>
    );
}

export default LearnAnswerSheetIsland;
